import React from 'react';
import { Typography, AutoComplete, Input } from 'antd';
import { CustomTooltip } from './custom-tooltip';
import { ResponsiveSelect } from '../../UI/inputs/responsive-select';

const { Text } = Typography;

interface Option {
  value: string | number;
  label?: React.ReactNode;
}

interface SelectFieldProps {
  field: {
    key: string;
    label: string;
    placeholder?: string;
    options?: Option[];
    defaultValue?: string | number;
    allowAutocompleteInput?: boolean;
    maxLengthCustomInput?: number;
    allowCustomInput?: boolean;
    showLiveOption?: boolean;
  };
  value: any;
  onChange: (value: any) => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
  excludeValues?: Array<string | number>;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error,
  excludeValues = []
}) => {
  const {
    label,
    placeholder,
    options,
    defaultValue,
    allowAutocompleteInput = false,
    maxLengthCustomInput = 40,
    allowCustomInput = false
  } = field;

  const toStr = (v: unknown) => String(v ?? '');

  const isExcluded = React.useCallback(
    (val: string | number) => excludeValues.some(ev => toStr(ev) === toStr(val)),
    [excludeValues]
  );

  // Normalizo opciones
  const safeOptions = React.useMemo<Option[]>(
    () =>
      (Array.isArray(options) ? options : [])
        .filter((o): o is Option => !!o && o.value !== undefined && o.value !== null)
        .map(o => ({ value: o.value, label: o.label ?? String(o.value) })),
    [options]
  );

  const visibleOptions = React.useMemo(
    () => safeOptions.filter(o => !isExcluded(o.value) || toStr(o.value) === toStr(value)),
    [safeOptions, isExcluded, value]
  );

  const defaultLabel = React.useMemo(() => {
    if (defaultValue === undefined) return '';
    const opt = safeOptions.find(o => toStr(o.value) === toStr(defaultValue));
    return String(opt?.label ?? defaultValue);
  }, [safeOptions, defaultValue]);

  const labelForValue = React.useCallback(
    (val: any) => {
      const opt = safeOptions.find(o => toStr(o.value) === toStr(val));
      return opt ? String(opt.label) : '';
    },
    [safeOptions]
  );

  // ======= AUTOCOMPLETE =======
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [typed, setTyped] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    if (!allowAutocompleteInput) return;
    const initial = value == null ? defaultLabel : labelForValue(value);
    setInput(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!allowAutocompleteInput) return;
    if (isEditing) return;
    const next = value == null ? defaultLabel : labelForValue(value);
    setInput(next);
  }, [allowAutocompleteInput, value, defaultLabel, labelForValue, isEditing]);

  const acOptions = React.useMemo(
    () => visibleOptions.map(o => ({ value: toStr(o.value), label: String(o.label) })),
    [visibleOptions]
  );

  const filtered = React.useMemo(() => {
    if (!allowAutocompleteInput) return [];
    const needle = input.trim().toLowerCase();
    if (!typed || !needle) return acOptions;
    return acOptions.filter(o => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle));
  }, [allowAutocompleteInput, acOptions, input, typed]);

  const resolveByText = React.useCallback(
    (txt: string) => {
      const t = txt.trim().toLowerCase();
      if (!t) return null;
      const byLabel = acOptions.find(o => o.label.toLowerCase() === t);
      if (byLabel) return byLabel.value;
      const byValue = acOptions.find(o => o.value.toLowerCase() === t);
      return byValue ? byValue.value : null;
    },
    [acOptions]
  );

  const handleFocus = () => {
    setIsEditing(true);
    setOpen(true);
    setTyped(false);
  };

  const handleClear = () => {
    setInput('');
    setTyped(true);
    setOpen(true);
  };

  const handleBlur = () => {
    setOpen(false);
    setTyped(false);
    setIsEditing(false);
    const current = input.trim();
    const resolved = resolveByText(current);

    if (resolved && (!isExcluded(resolved) || toStr(resolved) === toStr(value))) {
      if (toStr(value) !== toStr(resolved)) onChange(resolved);
      setInput(labelForValue(resolved));
    } else {
      if (defaultValue !== undefined && toStr(value) !== toStr(defaultValue)) {
        onChange(defaultValue);
      }
      setInput(defaultLabel);
    }
  };

  // ======= CUSTOM INPUT =======
  const [customText, setCustomText] = React.useState('');
  const isCustomSelected = allowCustomInput && (value === 'custom' || customText);

  const handleSelectChange = (val: any) => {
    if (allowCustomInput && val === 'custom') {
      onChange('custom');
      setCustomText('');
    } else {
      onChange(val);
      setCustomText('');
    }
  };

  const handleCustomTextChange = (text: string) => {
    setCustomText(text);
    if (text.trim()) {
      onChange(text);
    }
  };

  // ======= RENDER =======
  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {label}
      </Text>

      {allowCustomInput && isCustomSelected ? (
        // Custom input activo: mostrar select + input lado a lado
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <CustomTooltip field={field}>
              <ResponsiveSelect
                placeholder={placeholder}
                value='custom'
                onChange={handleSelectChange}
                options={visibleOptions}
                showSearch={false}
              />
            </CustomTooltip>
          </div>
          <div style={{ flex: 1 }}>
            <Input
              placeholder='Texto personalizado'
              style={{ fontSize: 12 }}
              value={customText}
              onChange={e => handleCustomTextChange(e.target.value)}
              status={error ? 'error' : undefined}
              maxLength={maxLengthCustomInput}
            />
          </div>
        </div>
      ) : allowAutocompleteInput ? (
        // Autocomplete
        <CustomTooltip field={field}>
          <AutoComplete
            style={{ width: '100%' }}
            options={filtered}
            value={input}
            open={open}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onSearch={txt => {
              setInput(txt);
              setTyped(true);
              setOpen(true);
            }}
            onChange={txt => {
              if (txt === '') handleClear();
              else {
                setInput(txt);
                setTyped(true);
                setOpen(true);
              }
            }}
            onSelect={val => {
              const lbl = labelForValue(val);
              setInput(lbl);
              setTyped(false);
              setOpen(false);
              setIsEditing(false);
              if (toStr(value) !== toStr(val)) onChange(val);
            }}
            placeholder={placeholder}
            status={error ? 'error' : undefined}
            allowClear
            maxLength={maxLengthCustomInput}
          >
            <Input />
          </AutoComplete>
        </CustomTooltip>
      ) : (
        // Select normal
        <CustomTooltip field={field}>
          <ResponsiveSelect
            placeholder={placeholder}
            value={value ?? defaultValue}
            onChange={allowCustomInput ? handleSelectChange : onChange}
            options={visibleOptions}
            status={error ? 'error' : undefined}
            showSearch={false}
            style={{ width: '100%', textAlign: 'left' }}
          />
        </CustomTooltip>
      )}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
